import React, { useEffect, useRef, useState } from 'react';
import { useGLTF, useAnimations } from '@react-three/drei';
import * as THREE from 'three';

const NintendoModel = ({ onPartClick, onHover, selectedPart }) => {
  const group = useRef();
  const { scene, animations } = useGLTF('/models/compressed.glb'); 
  const { actions } = useAnimations(animations, group);
  const [hasOpened, setHasOpened] = useState(false);
  const [highlightedMesh, setHighlightedMesh] = useState(null);

  // ✅ Auto-center & scale the model
  useEffect(() => {
    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    scene.position.sub(center);

    const maxDim = Math.max(size.x, size.y, size.z);
const scaleFactor = 0.1 / maxDim; // smaller value = smaller model
scene.scale.setScalar(scaleFactor);

  }, [scene]);

  // ✅ Save original materials for restoring later
  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.userData.originalMaterial = child.material.clone();
      }
    });
  }, [scene]);

  // ✅ Highlight selected part by RAW name
  useEffect(() => {
    // restore previous highlighted mesh
    if (highlightedMesh && highlightedMesh.userData.originalMaterial) {
      highlightedMesh.material = highlightedMesh.userData.originalMaterial;
    }

    if (!selectedPart) {
      setHighlightedMesh(null);
      return;
    }

    let matchedMesh = null;

    scene.traverse((child) => {
      if (child.isMesh && child.name === selectedPart) {
        matchedMesh = child;
      }
    });

    if (matchedMesh) {
      const highlightMaterial = new THREE.MeshStandardMaterial({ color: 0xffff00 });
      matchedMesh.material = highlightMaterial;
      setHighlightedMesh(matchedMesh);
    }
  }, [selectedPart, scene]);

  // ✅ Handle clicks
  const handleClick = (e) => {
    e.stopPropagation();
    const rawName = e.object.name;

    if (!hasOpened && animations.length > 0) {
      const openAction = actions[animations[0].name];
      openAction.reset().setLoop(THREE.LoopOnce).play();
      openAction.clampWhenFinished = true;
      setHasOpened(true);
    } else {
      if (onPartClick) {
        onPartClick(rawName); // RAW name
      }
    }
  };

  const handlePointerOver = (e) => {
    document.body.style.cursor = 'pointer';
    if (onHover) {
      onHover(e.object.name); // RAW name
    }
  };

  const handlePointerOut = () => {
    document.body.style.cursor = 'default';
    if (onHover) {
      onHover(null);
    }
  };

  return (
    <group
      ref={group}
      onClick={handleClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      dispose={null}
    >
      <primitive object={scene} />
    </group>
  );
};

export default NintendoModel;
